import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import { IconAI, IconMaximize, IconMinus, IconPowertrainGuideChevronDown, IconPowertrainGuideExternalLink, IconPowertrainGuideFrame41, IconPowertrainGuideLink, IconPowertrainGuideRestore, IconPowertrainGuideSend, IconPowertrainGuideStop, IconRefresh, IconRemove, IconWarning } from 'svgIcons/MotionPortalIcons';
import { isLikelyStructuredPayloadStream, stripQueryMetadataFromText, tryParseStructuredPayloadFromText } from 'helpers/copilotStructuredPayload';
import colors from 'theme/_colors.scss';
import MessageRenderer from './MessageRenderers';
import { DEFAULT_PANEL_DIMENSIONS, MAX_INPUT_LENGTH, MAXIMIZED_PANEL_DIMENSIONS, MINIMIZED_PANEL_DIMENSIONS, PANEL_GUTTER } from './constants';
import './style.scss';

const buildMessageId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const buildThreadId = () => typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `thread-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const clamp = (value, minValue, maxValue) => Math.min(Math.max(value, minValue), maxValue);

const toDroppedContent = (dataTransfer) => {
    if (!dataTransfer) {
        return '';
    }

    const plainText = (dataTransfer.getData('text/plain') || '').trim();
    const jsonText = (dataTransfer.getData('application/json') || '').trim();
    const uriText = (dataTransfer.getData('text/uri-list') || '').trim();
    const htmlText = (dataTransfer.getData('text/html') || '').trim();

    let extractedText = plainText || jsonText || uriText;

    if (!extractedText && htmlText) {
        extractedText = htmlText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    if (Array.isArray(dataTransfer.files) || dataTransfer.files?.length > 0) {
        const fileNames = Array.from(dataTransfer.files || [])
            .map((file) => file?.name)
            .filter(Boolean);

        if (fileNames.length > 0) {
            const filesSummary = `Files: ${fileNames.join(', ')}`;
            extractedText = extractedText ? `${extractedText}\n${filesSummary}` : filesSummary;
        }
    }

    return extractedText.trim();
};

const getPanelDimensions = ({ isExpanded, isMinimized }) => {
    if (typeof window === 'undefined') {
        return DEFAULT_PANEL_DIMENSIONS;
    }

    if (isMinimized) {
        return MINIMIZED_PANEL_DIMENSIONS;
    }

    if (isExpanded) {
        return {
            width: Math.min(MAXIMIZED_PANEL_DIMENSIONS.width, Math.max(360, window.innerWidth - PANEL_GUTTER * 2)),
            height: Math.min(MAXIMIZED_PANEL_DIMENSIONS.height, Math.max(260, window.innerHeight - PANEL_GUTTER * 2))
        };
    }

    return DEFAULT_PANEL_DIMENSIONS;
};

const getDefaultPanelPosition = ({ isExpanded, isMinimized }) => {
    if (typeof window === 'undefined') {
        return { x: PANEL_GUTTER, y: PANEL_GUTTER };
    }

    const { width, height } = getPanelDimensions({ isExpanded, isMinimized });
    return {
        x: Math.max(PANEL_GUTTER, window.innerWidth - width - PANEL_GUTTER),
        y: Math.max(PANEL_GUTTER, window.innerHeight - height - PANEL_GUTTER)
    };
};

const toTransportMessages = (messages, latestQuestion) => {
    const historyMessages = messages
        .filter(({ role, content, transportContent }) => role && typeof (transportContent || content) === 'string' && (transportContent || content).trim().length > 0)
        .map(({ role, content, transportContent }) => ({ role, content: transportContent || content }));

    return [...historyMessages, { role: 'user', content: latestQuestion }];
};

const PowertrainGuideAgentComponent = ({ isStructuredChatEnabled, labels, welcomeOptions, streamChat, testId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [panelPosition, setPanelPosition] = useState(() => getDefaultPanelPosition({ isExpanded: false, isMinimized: false }));
    const [isDragging, setIsDragging] = useState(false);
    const [isDropActive, setIsDropActive] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [error, setError] = useState('');
    const abortControllerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const panelRef = useRef(null);
    const inputRef = useRef(null);
    const dragMetaRef = useRef({ pointerOffsetX: 0, pointerOffsetY: 0 });
    const threadIdRef = useRef(null);
    const isMountedRef = useRef(true);
    const requestIdRef = useRef(0);
    const isInputTooLong = input.length > MAX_INPUT_LENGTH;

    useEffect(() => {
        if (isMinimized || !messagesEndRef.current) {
            return;
        }
        messagesEndRef.current.scrollIntoView({ behavior: isStreaming ? 'smooth' : 'auto' });
    }, [isMinimized, messages, isStreaming]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            requestIdRef.current += 1;
            abortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const syncPanelWithinViewport = () => {
            const fallbackDimensions = getPanelDimensions({ isExpanded, isMinimized });
            const panelRect = panelRef.current?.getBoundingClientRect();
            const panelWidth = panelRect?.width || fallbackDimensions.width;
            const panelHeight = panelRect?.height || fallbackDimensions.height;

            setPanelPosition((previousPosition) => ({
                x: clamp(previousPosition.x, PANEL_GUTTER, Math.max(PANEL_GUTTER, window.innerWidth - panelWidth - PANEL_GUTTER)),
                y: clamp(previousPosition.y, PANEL_GUTTER, Math.max(PANEL_GUTTER, window.innerHeight - panelHeight - PANEL_GUTTER))
            }));
        };

        syncPanelWithinViewport();
        window.addEventListener('resize', syncPanelWithinViewport);
        return () => window.removeEventListener('resize', syncPanelWithinViewport);
    }, [isOpen, isExpanded, isMinimized]);

    useEffect(() => {
        if (!isDragging) {
            return undefined;
        }

        const handlePointerMove = (event) => {
            const fallbackDimensions = getPanelDimensions({ isExpanded, isMinimized });
            const panelRect = panelRef.current?.getBoundingClientRect();
            const panelWidth = panelRect?.width || fallbackDimensions.width;
            const panelHeight = panelRect?.height || fallbackDimensions.height;

            const nextX = clamp(
                event.clientX - dragMetaRef.current.pointerOffsetX,
                PANEL_GUTTER,
                Math.max(PANEL_GUTTER, window.innerWidth - panelWidth - PANEL_GUTTER)
            );
            const nextY = clamp(
                event.clientY - dragMetaRef.current.pointerOffsetY,
                PANEL_GUTTER,
                Math.max(PANEL_GUTTER, window.innerHeight - panelHeight - PANEL_GUTTER)
            );

            setPanelPosition({ x: nextX, y: nextY });
        };

        const handlePointerUp = () => {
            document.body.style.userSelect = '';
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handlePointerMove);
        window.addEventListener('mouseup', handlePointerUp);
        return () => {
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', handlePointerMove);
            window.removeEventListener('mouseup', handlePointerUp);
        };
    }, [isDragging, isExpanded, isMinimized]);

    const closePanel = () => {
        requestIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        threadIdRef.current = null;
        setIsStreaming(false);
        setIsOpen(false);
        setIsDragging(false);
        setIsDropActive(false);
        setIsMinimized(false);
        setIsExpanded(false);
    };

    const openPanel = () => {
        threadIdRef.current = buildThreadId();
        setPanelPosition(getDefaultPanelPosition({ isExpanded: false, isMinimized: false }));
        setIsMinimized(false);
        setIsExpanded(false);
        setIsOpen(true);
    };

    const resetSession = () => {
        requestIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        threadIdRef.current = buildThreadId();
        setIsStreaming(false);
        setMessages([]);
        setInput('');
        setError('');
    };

    const toggleMinimize = () => {
        setIsMinimized((previousValue) => !previousValue);
    };

    const toggleExpand = () => {
        if (isMinimized) {
            setIsMinimized(false);
        }

        const nextIsExpanded = !isExpanded;
        setIsExpanded(nextIsExpanded);
        setPanelPosition((previousPosition) => {
            const defaultPosition = getDefaultPanelPosition({ isExpanded: nextIsExpanded, isMinimized: false });
            return {
                x: Math.min(previousPosition.x, defaultPosition.x),
                y: Math.min(previousPosition.y, defaultPosition.y)
            };
        });
    };

    const startDragging = (event) => {
        if (!isOpen) {
            return;
        }

        const eventTarget = event.target;
        if (eventTarget instanceof Element && eventTarget.closest('button')) {
            return;
        }

        event.preventDefault();

        const panelRect = panelRef.current?.getBoundingClientRect();
        const panelLeft = panelRect?.left || panelPosition.x;
        const panelTop = panelRect?.top || panelPosition.y;

        dragMetaRef.current = {
            pointerOffsetX: event.clientX - panelLeft,
            pointerOffsetY: event.clientY - panelTop
        };

        document.body.style.userSelect = 'none';
        setIsDragging(true);
    };

    const handleDropOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDropActive(true);
    };

    const handleDropLeave = () => {
        setIsDropActive(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDropActive(false);

        const droppedContent = toDroppedContent(event.dataTransfer);
        if (!droppedContent) {
            return;
        }

        setInput((previousInput) => previousInput ? `${previousInput}\n${droppedContent}` : droppedContent);
        setIsMinimized(false);

        window.requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    };

    const sendUserMessage = async ({ userPrompt, displayContent }) => {
        const trimmedPrompt = typeof userPrompt === 'string' ? userPrompt.trim() : '';
        if (!trimmedPrompt || isStreaming) {
            return;
        }

        const visibleContent = typeof displayContent === 'string' && displayContent.trim().length > 0 ? displayContent.trim() : trimmedPrompt;

        setError('');
        setInput('');

        const userMessageId = buildMessageId('user');
        const assistantMessageId = buildMessageId('assistant');
        const requestMessages = toTransportMessages(messages, trimmedPrompt);

        setMessages((previousMessages) => [
            ...previousMessages,
            { id: userMessageId, role: 'user', content: visibleContent, transportContent: trimmedPrompt },
            { id: assistantMessageId, role: 'assistant', content: '', rawContent: '', contentType: 'text', structuredData: null }
        ]);

        const controller = new AbortController();
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        abortControllerRef.current = controller;

        try {
            setIsStreaming(true);
            await streamChat({
                threadId: threadIdRef.current,
                messages: requestMessages,
                signal: controller.signal,
                onChunk: (chunkPayload) => {
                    if (!isMountedRef.current || controller.signal.aborted || requestIdRef.current !== requestId) {
                        return;
                    }

                    if (isStructuredChatEnabled && chunkPayload && typeof chunkPayload === 'object' && chunkPayload.type === 'structured') {
                        setMessages((previousMessages) => previousMessages.map((message) => {
                            if (message.id !== assistantMessageId) {
                                return message;
                            }

                            return {
                                ...message,
                                contentType: chunkPayload.contentType,
                                structuredData: chunkPayload.structuredData,
                                content: chunkPayload.fallbackText || message.content
                            };
                        }));

                        return;
                    }

                    if (chunkPayload && typeof chunkPayload === 'object' && chunkPayload.type === 'sources') {
                        setMessages((previousMessages) => previousMessages.map((message) => message.id === assistantMessageId
                            ? { ...message, sources: chunkPayload.sources, sourceUrls: chunkPayload.urls }
                            : message));

                        return;
                    }

                    if (chunkPayload && typeof chunkPayload === 'object' && chunkPayload.type === 'followups') {
                        setMessages((previousMessages) => previousMessages.map((message) => message.id === assistantMessageId
                            ? { ...message, followups: chunkPayload.followups }
                            : message));

                        return;
                    }

                    const delta = typeof chunkPayload === 'string' ? chunkPayload : chunkPayload?.delta || '';
                    setMessages((previousMessages) => previousMessages.map((message) => {
                        if (message.id !== assistantMessageId) {
                            return message;
                        }

                        const nextRawContent = `${message.rawContent || ''}${delta}`;
                        if (isStructuredChatEnabled) {
                            const parsedStructuredMessage = tryParseStructuredPayloadFromText(nextRawContent);
                            if (parsedStructuredMessage) {
                                return {
                                    ...message,
                                    rawContent: nextRawContent,
                                    contentType: parsedStructuredMessage.contentType,
                                    structuredData: parsedStructuredMessage.structuredData,
                                    content: parsedStructuredMessage.fallbackText
                                };
                            }
                        }

                        return {
                            ...message,
                            rawContent: nextRawContent,
                            contentType: 'text',
                            content: stripQueryMetadataFromText(nextRawContent)
                        };
                    }));
                }
            });
        } catch (requestError) {
            if (isMountedRef.current && requestIdRef.current === requestId && requestError?.name !== 'AbortError') {
                setError(requestError?.message || labels.error);
            }
        } finally {
            if (requestIdRef.current === requestId) {
                abortControllerRef.current = null;
                if (isMountedRef.current) {
                    setIsStreaming(false);
                }
            }
        }
    };

    const handleFeedbackSubmit = async ({ responsePayload, displayContent, sourceMessage }) => {
        if (!responsePayload) {
            return;
        }

        if (sourceMessage?.id) {
            setMessages((previousMessages) => previousMessages.map((message) => {
                if (message.id !== sourceMessage.id) {
                    return message;
                }

                return {
                    ...message,
                    structuredData: {
                        ...message.structuredData,
                        submitted: true,
                        submittedAt: new Date().toISOString(),
                        submittedAnswer: {
                            selectedOptionIds: responsePayload.selectedOptionIds || [],
                            comment: responsePayload.comment || ''
                        }
                    }
                };
            }));
        }

        await sendUserMessage({
            userPrompt: JSON.stringify(responsePayload),
            displayContent
        });
    };

    const submitMessage = async (event) => {
        event.preventDefault();
        if (isInputTooLong) {
            return;
        }
        await sendUserMessage({ userPrompt: input.trim() });
    };

    const stopStreaming = () => {
        requestIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setIsStreaming(false);
    };

    return (
        <>
            <div className='nav-item copilot-chat-menu' data-testid={testId}>
                <button
                    type='button'
                    className='copilot-chat-trigger-button'
                    onClick={openPanel}
                    title={labels.trigger}
                    aria-label={labels.trigger}
                >
                    <IconPowertrainGuideFrame41 width={48} height={48} />
                </button>
            </div>
            {isOpen &&
                <div
                    ref={panelRef}
                    className={`copilot-chat-panel ${isMinimized ? 'is-minimized' : ''} ${isExpanded ? 'is-expanded' : ''} ${isDropActive ? 'is-drop-active' : ''}`}
                    style={{ left: `${panelPosition.x}px`, top: `${panelPosition.y}px` }}
                    onDragOver={handleDropOver}
                    onDragEnter={handleDropOver}
                    onDragLeave={handleDropLeave}
                    onDrop={handleDrop}
                >
                    <div className='copilot-chat-panel-header' onMouseDown={startDragging}>
                        <div className='copilot-chat-panel-title'><IconAI width={20} height={20} />{labels.title}</div>
                        <div className='copilot-chat-panel-actions'>
                            {isMinimized
                                ? <button type='button' className='copilot-chat-panel-action' onClick={toggleMinimize} title={labels.restore} aria-label={labels.restore}>
                                    <IconPowertrainGuideRestore width={16} height={16} />
                                </button>
                                : <>
                                    <button type='button' className='copilot-chat-panel-action' onClick={resetSession} title={labels.refresh} aria-label={labels.refresh}>
                                        <IconRefresh width={16} height={16} />
                                    </button>
                                    <button type='button' className='copilot-chat-panel-action' onClick={toggleMinimize} title={labels.minimize} aria-label={labels.minimize}>
                                        <IconMinus />
                                    </button>
                                    <button type='button' className='copilot-chat-panel-action' onClick={toggleExpand} title={labels.expand} aria-label={labels.expand}>
                                        <IconMaximize width={14} height={14} />
                                    </button>
                                    <button type='button' className='copilot-chat-panel-action' onClick={closePanel} title={labels.close} aria-label={labels.close}>
                                        <IconRemove width={16} height={16} strokeWidth='4' />
                                    </button>
                                </>}
                        </div>
                    </div>
                    {!isMinimized &&
                        <div className='copilot-chat-content'>
                            <div className='copilot-chat-messages'>
                                {messages.length === 0 &&
                                    <div className='copilot-chat-welcome'>
                                        <h2>{labels.welcomeTitle}</h2>
                                        <p>{labels.welcomeSubtitle}</p>
                                        <div className='copilot-chat-welcome-options'>
                                            {welcomeOptions.map((option) => {
                                                const optionContent = <>
                                                    {option.icon && <span className='copilot-chat-welcome-icon'>{option.icon}</span>}
                                                    <span>{option.label}</span>
                                                    {option.showExternalLink && <IconPowertrainGuideExternalLink width={20} height={20} />}
                                                </>;

                                                return option.href
                                                    ? <a key={option.id} className='copilot-chat-welcome-option' href={option.href} target='_blank' rel='noopener noreferrer'>{optionContent}</a>
                                                    : <button key={option.id} type='button' className='copilot-chat-welcome-option' onClick={() => sendUserMessage({ userPrompt: option.prompt, displayContent: option.label })}>{optionContent}</button>;
                                            })}
                                        </div>
                                    </div>}
                                {messages.map((message) => {
                                    if (isStructuredChatEnabled && message?.role === 'assistant' && message?.contentType !== 'text' && message?.structuredData) {
                                        return <MessageRenderer key={message.id} message={message} labels={labels} onFeedbackSubmit={(payload) => handleFeedbackSubmit({ ...payload, sourceMessage: message })} />;
                                    }

                                    const assistantPlaceholder = message.role === 'assistant' && isStreaming ? labels.thinking : '';
                                    const shouldHideRawStructuredStream = isStructuredChatEnabled
                                        && message?.role === 'assistant'
                                        && isStreaming
                                        && message?.contentType === 'text'
                                        && isLikelyStructuredPayloadStream(message?.content);
                                    const displayContent = shouldHideRawStructuredStream
                                        ? labels.thinking
                                        : message.content || assistantPlaceholder;

                                    return (
                                        <div key={message.id} className={`copilot-chat-message ${message.role === 'user' ? 'user' : 'assistant'}`}>
                                            {displayContent}
                                            {message.role === 'assistant' && Array.isArray(message.sources) && message.sources.length > 0 &&
                                                <div className='copilot-chat-sources'>
                                                    <div className='copilot-chat-sources-title'>
                                                        <IconPowertrainGuideChevronDown width={16} height={16} aria-hidden='true' />
                                                        <span>{labels.sources} ({message.sources.length})</span>
                                                    </div>
                                                    <div className='copilot-chat-sources-list'>
                                                        {message.sources.map((source, index) => {
                                                            const sourceUrl = message.sourceUrls?.[index];
                                                            return (
                                                                <div key={`${message.id}-source-${index}`} className='copilot-chat-source-item'>
                                                                    <IconPowertrainGuideLink width={16} height={16} aria-hidden='true' />
                                                                    {sourceUrl
                                                                        ? <a href={sourceUrl} target='_blank' rel='noopener noreferrer' title={source}>{source}</a>
                                                                        : <span title={source}>{source}</span>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>}
                                            {message.role === 'assistant' && Array.isArray(message.followups) && message.followups.length > 0 &&
                                                <div className='copilot-chat-followups'>
                                                    <div className='copilot-chat-followups-title'>{labels.followups}</div>
                                                    <div className='copilot-chat-followups-list'>
                                                        {message.followups.map((followup, index) => {
                                                            return (
                                                                <button
                                                                    key={`${message.id}-followup-${index}`}
                                                                    type='button'
                                                                    className='copilot-chat-followup-chip'
                                                                    disabled={isStreaming}
                                                                    onClick={() => sendUserMessage({ userPrompt: followup })}
                                                                >
                                                                    {followup}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>}
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>
                            {error && <div className='copilot-chat-error'>{error}</div>}
                            <form className='copilot-chat-input-row' onSubmit={submitMessage}>
                                <textarea
                                    ref={inputRef}
                                    className={`copilot-chat-textarea ${isStreaming ? 'is-streaming' : ''} ${isInputTooLong ? 'has-error' : ''}`}
                                    value={input}
                                    onChange={(event) => setInput(event.target.value)}
                                    placeholder={labels.inputPlaceholder}
                                    rows={3}
                                    disabled={isStreaming}
                                />
                                <button
                                    className={`copilot-chat-send-button ${isStreaming ? 'is-streaming' : ''}`}
                                    type={isStreaming ? 'button' : 'submit'}
                                    onClick={isStreaming ? stopStreaming : undefined}
                                    aria-label={isStreaming ? labels.thinking : labels.send}
                                    disabled={!isStreaming && (input.trim().length === 0 || isInputTooLong)}
                                >
                                    {isStreaming ? <IconPowertrainGuideStop width={16} height={16} /> : <IconPowertrainGuideSend width={16} height={16} />}
                                </button>
                                {isInputTooLong &&
                                    <Tippy content={labels.maxLengthError} placement='top' theme='light-border'>
                                        <span className='copilot-chat-char-limit-icon'>
                                            <IconWarning width={16} height={16} color={colors.red} />
                                        </span>
                                    </Tippy>}
                            </form>
                            {isInputTooLong &&
                                <div className='copilot-chat-error' role='alert'>
                                    <IconWarning width={14} height={14} color={colors.red} />
                                    <span>{labels.maxLengthError}</span>
                                </div>}
                            <div className='copilot-chat-disclaimer'>{labels.disclaimer}</div>
                        </div>
                    }
                </div>
            }
        </>
    );
};

PowertrainGuideAgentComponent.propTypes = {
    isStructuredChatEnabled: PropTypes.bool,
    labels: PropTypes.shape({
        title: PropTypes.string.isRequired,
        trigger: PropTypes.string.isRequired,
        inputPlaceholder: PropTypes.string.isRequired,
        welcomeTitle: PropTypes.string.isRequired,
        welcomeSubtitle: PropTypes.string.isRequired,
        thinking: PropTypes.string.isRequired,
        send: PropTypes.string.isRequired,
        sources: PropTypes.string.isRequired,
        followups: PropTypes.string.isRequired,
        minimize: PropTypes.string.isRequired,
        restore: PropTypes.string.isRequired,
        expand: PropTypes.string.isRequired,
        close: PropTypes.string.isRequired,
        refresh: PropTypes.string.isRequired,
        error: PropTypes.string.isRequired,
        selectAtLeast: PropTypes.string.isRequired,
        options: PropTypes.string.isRequired,
        option: PropTypes.string.isRequired,
        feedback: PropTypes.string.isRequired,
        feedbackSubmitted: PropTypes.string.isRequired,
        submit: PropTypes.string.isRequired,
        assistantImage: PropTypes.string.isRequired,
        imageUnavailable: PropTypes.string.isRequired,
        pdfUnavailable: PropTypes.string.isRequired,
        attachedDocument: PropTypes.string.isRequired,
        download: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        disclaimer: PropTypes.string.isRequired,
        maxLengthError: PropTypes.string.isRequired
    }).isRequired,
    welcomeOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        prompt: PropTypes.string.isRequired,
        icon: PropTypes.node,
        href: PropTypes.string,
        showExternalLink: PropTypes.bool
    })),
    streamChat: PropTypes.func.isRequired,
    testId: PropTypes.string
};

PowertrainGuideAgentComponent.defaultProps = {
    isStructuredChatEnabled: false,
    welcomeOptions: [],
    testId: 'powertrain-guide-agent'
};

export default PowertrainGuideAgentComponent;
